import { useQuery } from '@tanstack/react-query'
import {
	ActionBar,
	AddIcon,
	Button,
	CollapsibleSection,
	Select,
	SpinnerWithBackground,
	Text,
	UnsavedChangesDialog,
	useUnsavedChangesWarning,
} from '@viapeople/components'
import { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { useToast } from '../../../utils/ToastContext'
import { trpc } from '../../../utils/trpc'
import { useEnpsStore } from '../store'
import type { EnpsEmailTemplateTabInput } from './types'

/**
 * @fileoverview eNPS Email Template Tab
 *
 * Configure email template for survey invitations
 */

// Available email variables for eNPS
const ENPS_VARIABLES = [
	{
		key: '{{enps.surveyUrl}}',
		label: 'NPS Survey Link',
		description: 'Direct link for participants to submit their NPS response',
		example: 'https://app.example.com/enps/response/abc123',
		required: true,
	},
	{
		key: '{{enps.questionText}}',
		label: 'Survey Question',
		description: 'The main NPS question text',
		example: 'How likely are you to recommend our organization as a place to work?',
	},
	{
		key: '{{enps.name}}',
		label: 'Survey Name',
		description: 'The name of the NPS survey configuration',
		example: 'Q4 2024 Employee Satisfaction Survey',
	},
	{
		key: '{{users.firstName}}',
		label: 'User First Name',
		description: "The participant's first name",
		example: 'John',
	},
	{
		key: '{{users.lastName}}',
		label: 'User Last Name',
		description: "The participant's last name",
		example: 'Doe',
	},
	{
		key: '{{users.fullName}}',
		label: 'User Full Name',
		description: "The participant's full name",
		example: 'John Doe',
	},
	{
		key: '{{organizations.name}}',
		label: 'Organization Name',
		description: 'The organization name',
		example: 'Acme Corp',
	},
]

export const EnpsEmailTemplateTab = () => {
	const { organizationId, enpsId } = useParams<{ organizationId: string; enpsId: string }>()
	const navigate = useNavigate()
	const toast = useToast()
	const {
		enpsConfig,
		useEditEnpsConfig,
		selectedTemplate,
		isLoadingTemplate,
		previewSubject,
		previewBody,
		fetchSelectedTemplate,
	} = useEnpsStore()
	const { editEnpsConfig, isLoading: isEditing } = useEditEnpsConfig(enpsConfig?.id)

	// Fetch email templates
	const { data: templates, isLoading: isLoadingTemplates } = useQuery({
		...trpc.admin.emailTemplates.search.queryOptions({
			organizationId: organizationId as string,
			page: 1,
			perPage: 100,
		}),
		enabled: !!organizationId,
	})

	const {
		handleSubmit,
		control,
		watch,
		formState: { errors, isDirty },
		reset,
	} = useForm<EnpsEmailTemplateTabInput>({
		defaultValues: {
			emailTemplateId: enpsConfig?.emailTemplateId || '',
		},
	})

	const { isBlocked, confirmLeave, cancelLeave } = useUnsavedChangesWarning(isDirty)

	const selectedTemplateId = watch('emailTemplateId')

	useEffect(() => {
		if (enpsConfig) {
			reset({
				emailTemplateId: enpsConfig.emailTemplateId || '',
			})
		}
	}, [enpsConfig, reset])

	// Fetch selected template when template ID changes
	useEffect(() => {
		if (selectedTemplateId && organizationId) {
			fetchSelectedTemplate(selectedTemplateId, organizationId)
		} else {
			fetchSelectedTemplate('', '')
		}
	}, [selectedTemplateId, organizationId, fetchSelectedTemplate])

	const handleSubmitInternal = async (data: EnpsEmailTemplateTabInput) => {
		if (!enpsConfig?.id) {
			toast.showToast('Please create the NPS survey first (General tab)', 'error')
			return
		}

		// Validate that survey link is in the template if template is selected (case-insensitive)
		if (data.emailTemplateId && selectedTemplate) {
			const hasSurveyLink =
				selectedTemplate.subject?.toLowerCase().includes('{{enps.surveyurl}}') ||
				selectedTemplate.body?.toLowerCase().includes('{{enps.surveyurl}}')

			if (!hasSurveyLink) {
				toast.showToast('Email template must include {{enps.surveyUrl}} variable', 'error')
				return
			}
		}

		await editEnpsConfig({
			emailTemplateId: data.emailTemplateId || null,
		})
		reset(data)
	}

	const templateOptions =
		templates?.data.map((tmpl) => ({
			value: tmpl.id,
			label: tmpl.name,
		})) || []

	const handleInsertVariable = (variable: string) => {
		// Copy the variable to clipboard
		navigator.clipboard.writeText(variable)
		toast.showToast(`Copied ${variable} to clipboard`)
	}

	const isPending = isEditing || isLoadingTemplate

	return (
		<form onSubmit={handleSubmit(handleSubmitInternal)} className='relative pb-24 mt-6 w-full'>
			{isPending && <SpinnerWithBackground />}
			<div className='grid grid-cols-1 gap-8 px-6 max-w-6xl'>
				{/* Template Selection */}
				<div className='space-y-4'>
					<Controller
						name='emailTemplateId'
						control={control}
						render={({ field }) => (
							<Select
								label='Select Email Template (Optional)'
								placeholder='Choose an email template'
								options={templateOptions}
								value={field.value || ''}
								onChange={field.onChange}
								variant={errors.emailTemplateId ? 'error' : 'default'}
								hint={errors.emailTemplateId?.message || 'Select a template or leave empty to skip email notifications'}
								disabled={isLoadingTemplates}
							/>
						)}
					/>

					<div className='flex items-center gap-2'>
						{selectedTemplateId && (
							<Button
								variant='secondary'
								size='small'
								onClick={() => navigate(`/organizations/${organizationId}/email-templates/${selectedTemplateId}`)}>
								Edit Template
							</Button>
						)}
						<Button
							variant='secondary'
							size='small'
							onClick={() => navigate(`/organizations/${organizationId}/email-templates/create`)}>
							<AddIcon className='h-4 w-4 mr-1' />
							Create New Template
						</Button>
					</div>
				</div>

				{/* Available Variables */}
				<CollapsibleSection title='Available Email Variables' defaultOpen={true}>
					<div className='space-y-3'>
						<Text variant='xsmall' className='text-gray-600'>
							Click on a variable to copy it to your clipboard. Use these in your email template to personalize
							messages.
						</Text>
						<div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
							{ENPS_VARIABLES.map((variable) => (
								<button
									key={variable.key}
									type='button'
									onClick={() => handleInsertVariable(variable.key)}
									className='flex items-start gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors text-left'>
									<div className='flex-1'>
										<div className='flex items-center gap-2'>
											<Text variant='xsmall' weight='semibold' className='font-mono'>
												{variable.key}
											</Text>
											{variable.required && (
												<span className='px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded'>Required</span>
											)}
										</div>
										<Text variant='xsmall' className='text-gray-600 mt-1'>
											{variable.description}
										</Text>
										<Text variant='xsmall' className='text-gray-500 mt-1 italic'>
											Example: {variable.example}
										</Text>
									</div>
								</button>
							))}
						</div>
					</div>
				</CollapsibleSection>

				{/* Template Preview */}
				{selectedTemplate && (
					<div className='space-y-4'>
						<Text variant='small' weight='semibold'>
							Email Preview
						</Text>
						<div className='border rounded-lg overflow-hidden'>
							{/* Email Header */}
							<div className='bg-gray-100 p-4 border-b'>
								<Text variant='xsmall' className='text-gray-600'>
									Subject
								</Text>
								<Text variant='small' weight='semibold' className='mt-1'>
									{previewSubject || '(No subject)'}
								</Text>
							</div>

							{/* Email Body */}
							<div className='p-6 bg-white'>
								<div
									className='prose prose-sm max-w-none'
									// biome-ignore lint/security/noDangerouslySetInnerHtml: HTML is sanitized email template preview
									dangerouslySetInnerHTML={{ __html: previewBody || '(Empty body)' }}
								/>
							</div>
						</div>

						<Text variant='xsmall' className='text-gray-600 italic'>
							This preview shows example data. Actual emails will use real participant data.
						</Text>
					</div>
				)}

				{/* Info Box */}
				<div className='bg-blue-50 p-4 rounded-lg'>
					<Text variant='small' weight='semibold' className='text-blue-900'>
						💡 Email Template Tips
					</Text>
					<ul className='mt-2 ml-6 space-y-2 list-disc text-blue-800 text-xs'>
						<li>
							<strong>Required:</strong> The <code>{'{{enps.surveyUrl}}'}</code> variable must be included in your email
							template
						</li>
						<li>Emails will be sent when the NPS survey is launched to participants</li>
						<li>You can skip email notifications by not selecting a template</li>
						<li>Participants will receive a unique link to submit their NPS score and feedback</li>
					</ul>
				</div>
			</div>
			<ActionBar
				rightActions={[
					{
						variant: 'secondary' as const,
						label: 'Back',
						onClick: () => navigate(`/organizations/${organizationId}/enps/${enpsId}/schedule`),
					},
					{
						variant: 'primary' as const,
						label: 'Save',
						onClick: handleSubmit(handleSubmitInternal),
					},
				]}
			/>
			<UnsavedChangesDialog isBlocked={isBlocked} confirmLeave={confirmLeave} cancelLeave={cancelLeave} />
		</form>
	)
}
